import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client/react'
import Button from '../ui/Button'
import Input from '../ui/Input'
import FormError from '../ui/FormError'
import AvatarUploader from './AvatarUploader'
import { useAuth } from '../../contexts/AuthContext'
import { useLang } from '../../contexts/LangContext'
import { UPDATE_PROFILE_MUTATION } from '../../graphql/auth'

interface UpdateProfileData {
  updateProfile: {
    user: {
      id: string
      locale: string
      avatarUrl: string | null
    } | null
    errors: string[]
  }
}

interface UpdateProfileVariables {
  firstName?: string
  lastName?: string
  phone?: string
  avatarUrl?: string
  locale?: string
}

interface FormState {
  firstName: string
  lastName: string
  phone: string
  avatarUrl: string
  locale: 'el' | 'en'
}

const PHONE_FORMAT = /^\+?[\d\s\-().]{7,20}$/

export default function ProfileInfoForm() {
  const { currentUser, refetchUser } = useAuth()
  const { locale, setLocale, t } = useLang()
  const [form, setForm] = useState<FormState>({
    firstName: currentUser?.firstName ?? '',
    lastName: currentUser?.lastName ?? '',
    phone: currentUser?.phone ?? '',
    avatarUrl: currentUser?.avatarUrl ?? '',
    locale: (currentUser?.locale === 'en' ? 'en' : 'el'),
  })
  const [errors, setErrors] = useState<string[]>([])
  const [saved, setSaved] = useState(false)
  const [updateProfile, { loading }] = useMutation<UpdateProfileData, UpdateProfileVariables>(UPDATE_PROFILE_MUTATION)

  useEffect(() => {
    if (!currentUser) return

    setForm({
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      phone: currentUser.phone ?? '',
      avatarUrl: currentUser.avatarUrl ?? '',
      locale: currentUser.locale === 'en' ? 'en' : 'el',
    })
  }, [currentUser])

  async function handleSave() {
    const nextErrors: string[] = []
    if (!form.firstName.trim()) nextErrors.push(t('Το όνομα είναι υποχρεωτικό.', 'First name is required.'))
    if (!form.lastName.trim()) nextErrors.push(t('Το επώνυμο είναι υποχρεωτικό.', 'Last name is required.'))
    if (form.phone.trim() && !PHONE_FORMAT.test(form.phone.trim())) {
      nextErrors.push(t('Το τηλέφωνο δεν είναι έγκυρο.', 'Phone number is invalid.'))
    }

    if (nextErrors.length > 0) {
      setErrors(nextErrors)
      setSaved(false)
      return
    }

    try {
      const { data } = await updateProfile({
        variables: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phone: form.phone.trim() || undefined,
          avatarUrl: form.avatarUrl || undefined,
          locale: form.locale,
        },
      })

      const mutationErrors = data?.updateProfile.errors ?? [t('Δεν ήταν δυνατή η αποθήκευση.', 'Could not save your profile.')]
      if (mutationErrors.length > 0) {
        setErrors(mutationErrors)
        setSaved(false)
        return
      }

      setErrors([])
      setSaved(true)
      if (form.locale !== locale) setLocale(form.locale)
      await refetchUser()
    } catch {
      setErrors([t('Δεν ήταν δυνατή η αποθήκευση.', 'Could not save your profile.')])
      setSaved(false)
    }
  }

  return (
    <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-950">{t('Στοιχεία προφίλ', 'Profile info')}</h2>
          <p className="mt-2 text-sm leading-7 text-slate-600">
            {t('Ενημέρωσε τα βασικά στοιχεία λογαριασμού και τη γλώσσα προτίμησης.', 'Update your account details and preferred language.')}
          </p>
        </div>
        <Button type="button" onClick={handleSave} loading={loading}>
          {t('Αποθήκευση', 'Save')}
        </Button>
      </div>

      <div className="mt-6 space-y-4">
        <FormError errors={errors} />
        {saved && (
          <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
            {t('Οι αλλαγές αποθηκεύτηκαν.', 'Profile changes saved.')}
          </div>
        )}

        <AvatarUploader
          value={form.avatarUrl}
          onChange={(avatarUrl) => {
            setSaved(false)
            setForm((current) => ({ ...current, avatarUrl }))
          }}
        />

        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label={t('Όνομα', 'First name')}
            value={form.firstName}
            onChange={(event) => {
              setSaved(false)
              setForm((current) => ({ ...current, firstName: event.target.value }))
            }}
            required
          />
          <Input
            label={t('Επώνυμο', 'Last name')}
            value={form.lastName}
            onChange={(event) => {
              setSaved(false)
              setForm((current) => ({ ...current, lastName: event.target.value }))
            }}
            required
          />
          <Input
            label={t('Τηλέφωνο', 'Phone')}
            value={form.phone}
            onChange={(event) => {
              setSaved(false)
              setForm((current) => ({ ...current, phone: event.target.value }))
            }}
            placeholder="+30 69..."
          />

          <div className="flex flex-col gap-1.5">
            <label htmlFor="settings-locale" className="text-sm font-medium text-slate-700">
              {t('Γλώσσα', 'Language')}
            </label>
            <select
              id="settings-locale"
              value={form.locale}
              onChange={(event) => {
                setSaved(false)
                setForm((current) => ({ ...current, locale: event.target.value === 'en' ? 'en' : 'el' }))
              }}
              className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 transition-colors hover:border-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="el">Ελληνικά</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
      </div>
    </section>
  )
}
